import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
  ViewChild,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Headline, Modal, Subheadline } from '@spirit-testing/component-library';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { MatDivider } from '@angular/material/divider';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatIcon } from '@angular/material/icon';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { EMPTY, forkJoin, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, defaultIfEmpty, map, switchMap, take, tap } from 'rxjs/operators';
import { ProjectIntegrationService } from '../services/project-integration.service';
import { ProjectIntegration } from '../models/project-integration.model';
import { ProjectIntegrationFormComponent } from './project-integration-form/project-integration-form.component';
import { HeaderColorService } from '../services/header-color.service';
import { DEFAULT_LOGO_PATH, HeaderLogoService } from '../services/header-logo.service';
import { HeaderNameService } from '../services/header-name.service';
import { BRANDING_LOGO_TYPES, BrandingApiService } from '../services/branding-api.service';
import { AccountService } from '../account/account.service';
import { TenantUserService } from '../services/tenant-user.service';
import { BrandingData } from '../models/branding.model';
import { CustomerUserDTO, RoleDTO } from '../models/customer.model';
import { ChangeRoleDialogComponent } from '../shared/change-role-dialog/change-role-dialog.component';
import { AvatarComponent } from '../components/avatar/avatar.component';
import { FilterChipComponent } from '../components/filter-chip/filter-chip.component';
import { SearchBarComponent } from '../components/search-bar/search-bar.component';
import { ScrollContainerDirective } from '../shared/scroll-container/scroll-container.directive';
import { KiMarkComponent } from '../components/ki/ki-mark/ki-mark.component';
import { KiMandantSectionComponent } from './ki-mandant-section/ki-mandant-section.component';
import { warnBeforeUnload } from '../shared/forms/warn-before-unload';

type SettingsReiter = 'allgemein' | 'ki';

const HEX_COLOR_PATTERN = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;

const LOGO_MAX_BYTES = 5 * 1024 * 1024;
const LOGO_FORMAT_ERROR = 'settings.logoFormatError';
const LOGO_TOO_LARGE_ERROR = 'settings.logoTooLargeError';

@Component({
  selector: 'app-settings',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    Headline,
    Subheadline,
    Modal,
    TranslatePipe,
    MatButton,
    MatIconButton,
    MatTooltip,
    MatDivider,
    MatIcon,
    MatTableModule,
    MatPaginator,
    MatProgressSpinnerModule,
    ChangeRoleDialogComponent,
    ProjectIntegrationFormComponent,
    AvatarComponent,
    SearchBarComponent,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    ScrollContainerDirective,
    KiMarkComponent,
    KiMandantSectionComponent,
  ],
  templateUrl: './settings.component.html',
  styleUrl: './settings.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SettingsComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private fb = inject(NonNullableFormBuilder);
  private brandingApiService = inject(BrandingApiService);
  private headerColorService = inject(HeaderColorService);
  protected headerLogoService = inject(HeaderLogoService);
  private headerNameService = inject(HeaderNameService);
  private snackBar = inject(MatSnackBar);
  private translate = inject(TranslateService);
  private accountService = inject(AccountService);
  private tenantUserService = inject(TenantUserService);
  private integrationService = inject(ProjectIntegrationService);

  private initialColor = signal(this.headerColorService.color());
  private initialName = signal(this.headerNameService.name());
  protected readonly firmenname = this.initialName.asReadonly();
  private readonly kiSection = viewChild(KiMandantSectionComponent);
  private readonly deactivateConfirm$ = new Subject<boolean>();
  private logoDeletePending = signal(false);
  private pendingLogo = signal<{ file: File; previewUrl: string } | null>(null);
  readonly logoError = signal<string | null>(null);
  readonly logoAccept = BRANDING_LOGO_TYPES.join(',');
  private currentUserId = signal<string | null>(null);
  private currentCustomerId = signal<string | null>(null);
  private currentSearchTerm = signal('');

  readonly roleFilterOptions: readonly { code: string; labelKey: string }[] = [
    { code: 'PLATFORM_ADMIN', labelKey: 'roles.PLATFORM_ADMIN' },
    { code: 'TENANT_ADMIN', labelKey: 'roles.TENANT_ADMIN' },
    { code: 'USER', labelKey: 'roles.USER' },
  ];

  readonly colorPresets: readonly string[] = ['#2C5591', '#1E3E6C', '#0F766E', '#5B21B6', '#BE123C', '#0F172A'];

  readonly projectId = toSignal(this.route.parent?.paramMap.pipe(map((p) => p.get('projectId'))) ?? of(null), {
    initialValue: null,
  });

  readonly projectName = toSignal(
    this.route.parent?.data.pipe(map((d) => d['breadcrumb'] as string | null)) ?? of(null),
    { initialValue: null },
  );

  readonly projectIntegrations = signal<ProjectIntegration[]>([]);

  form = this.fb.group({
    color: [this.initialColor(), [Validators.required, Validators.pattern(HEX_COLOR_PATTERN)]],
    name: [this.initialName(), [Validators.required, Validators.maxLength(40)]],
  });

  private formValues = toSignal(this.form.valueChanges, { initialValue: this.form.value });

  showDeleteModal = signal<boolean>(false);
  canManageTenantUsers = signal(false);
  roles = signal<RoleDTO[]>([]);
  usersLoading = signal(false);
  usersError = signal<string | null>(null);
  selectedUser = signal<CustomerUserDTO | null>(null);
  showRoleDialog = signal(false);
  activeRoleFilter = signal<string | null>(null);

  dataSource = new MatTableDataSource<CustomerUserDTO>([]);
  readonly userTableColumns = ['name', 'email', 'role', 'action'];

  hasChanges = computed(() => {
    const values = this.formValues();
    return (
      values.color !== this.initialColor() ||
      values.name !== this.initialName() ||
      this.logoDeletePending() ||
      this.pendingLogo() !== null
    );
  });

  readonly reiter = signal<SettingsReiter>('allgemein');
  readonly kiGeoeffnet = signal(false);
  readonly showDeactivateConfirm = signal(false);

  private kiGeaendert = computed(() => this.kiSection()?.istGeaendert() ?? false);
  readonly hatSpeicherbareAenderungen = computed(() => this.hasChanges() || this.kiGeaendert());
  /** Für die Rückfrage beim Verlassen zählt auch ein noch nicht hinzugefügtes Muster im Reiter KI. */
  readonly hatUngespeicherteAenderungen = computed(
    () => this.hatSpeicherbareAenderungen() || (this.kiSection()?.hatEntwurf() ?? false),
  );
  readonly kiUngueltig = computed(() => this.kiGeaendert() && !(this.kiSection()?.istGueltig() ?? true));

  previewLogoPath = computed(() => {
    if (this.logoDeletePending()) return DEFAULT_LOGO_PATH;
    return this.pendingLogo()?.previewUrl ?? this.headerLogoService.logoSrc();
  });
  readonly previewBackground = computed(() => {
    const color = this.formValues().color ?? '';
    return HEX_COLOR_PATTERN.test(color) ? color : this.initialColor();
  });

  @ViewChild(MatPaginator) set paginator(value: MatPaginator) {
    if (value) {
      this.dataSource.paginator = value;
    }
  }

  constructor() {
    warnBeforeUnload(() => this.hatUngespeicherteAenderungen());
    this.destroyRef.onDestroy(() => this.dropPendingLogo());
  }

  ngOnInit(): void {
    this.dataSource.filterPredicate = (user, filter) => {
      const [term, roleCode] = filter.split('\u0000');
      const matchesSearch =
        !term || user.displayName.toLowerCase().includes(term) || user.email.toLowerCase().includes(term);
      const matchesRole = !roleCode || user.role.toLowerCase() === roleCode;
      return matchesSearch && matchesRole;
    };

    this.form.controls.color.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((val) => {
      if (val && val !== val.toUpperCase()) {
        this.form.controls.color.setValue(val.toUpperCase(), { emitEvent: false });
      }
    });

    this.accountService
      .getProfile()
      .pipe(
        switchMap((profile) => {
          this.currentUserId.set(profile.id);
          if (profile.role !== 'TENANT_ADMIN' && profile.role !== 'PLATFORM_ADMIN') return EMPTY;
          this.canManageTenantUsers.set(true);
          this.currentCustomerId.set(profile.customerId);
          this.usersLoading.set(true);
          return forkJoin({
            users: this.tenantUserService.getUsers(profile.customerId),
            roles: this.tenantUserService.getRoles(profile.customerId),
          });
        }),
      )
      .subscribe({
        next: ({ users, roles }) => {
          this.dataSource.data = users;
          this.roles.set(roles);
          this.usersLoading.set(false);
        },
        error: () => {
          this.usersError.set('settings.userTable.error');
          this.usersLoading.set(false);
        },
      });

    this.route.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => this.reiterAnzeigen(params.get('reiter') === 'ki' ? 'ki' : 'allgemein'));

    const parentParamMap$ = this.route.parent?.paramMap;
    if (parentParamMap$) {
      parentParamMap$
        .pipe(
          map((p) => p.get('projectId')),
          switchMap((id) => (id ? this.integrationService.list(id).pipe(catchError(() => of([]))) : of([]))),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe((integrations) => this.projectIntegrations.set(integrations));
    }
  }

  onReiterWaehlen(reiter: SettingsReiter): void {
    this.reiterAnzeigen(reiter);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { reiter: reiter === 'ki' ? 'ki' : null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private reiterAnzeigen(reiter: SettingsReiter): void {
    this.reiter.set(reiter);
    if (reiter === 'ki') this.kiGeoeffnet.set(true);
  }

  onIntegrationSaved(integration: ProjectIntegration): void {
    const exists = this.projectIntegrations().some((i) => i.id === integration.id);
    if (exists) {
      this.projectIntegrations.update((list) => list.map((i) => (i.id === integration.id ? integration : i)));
    } else {
      this.projectIntegrations.update((list) => [...list, integration]);
    }
  }

  onPresetClick(preset: string): void {
    this.form.controls.color.setValue(preset);
    this.form.controls.color.markAsTouched();
  }

  onSearchValueChange(value: string): void {
    const term = value.trim().toLowerCase();
    this.currentSearchTerm.set(term);
    this.applyTableFilter(term, this.activeRoleFilter()?.toLowerCase() ?? '');
  }

  setRoleFilter(code: string | null): void {
    this.activeRoleFilter.set(code);
    this.applyTableFilter(this.currentSearchTerm(), code?.toLowerCase() ?? '');
  }

  private applyTableFilter(search: string, roleCode: string): void {
    this.dataSource.filter = roleCode ? `${search}\x00${roleCode}` : search;
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  onChangeRole(user: CustomerUserDTO): void {
    this.selectedUser.set(user);
    this.showRoleDialog.set(true);
  }

  onRoleSave(roleId: string): void {
    const user = this.selectedUser();
    const customerId = this.currentCustomerId();
    if (!user || !customerId) return;

    this.tenantUserService.assignTenantRole(customerId, user.id, roleId).subscribe({
      next: (updated) => {
        this.dataSource.data = this.dataSource.data.map((u) => (u.id === updated.id ? updated : u));
        this.showRoleDialog.set(false);
        this.snackBar.open(this.translate.instant('settings.roleChangeSuccess'), undefined, {
          duration: 3000,
          verticalPosition: 'top',
          panelClass: 'snackbar-success',
        });
      },
      error: (err) => {
        const key = err?.status === 409 ? 'settings.lastAdminError' : 'settings.roleChangeError';
        this.snackBar.open(this.translate.instant(key), undefined, {
          duration: 3000,
          verticalPosition: 'top',
          panelClass: 'snackbar-error',
        });
      },
    });
  }

  onRoleDialogCancel(): void {
    this.showRoleDialog.set(false);
  }

  isOwnUser(userId: string): boolean {
    return userId === this.currentUserId();
  }

  getRoleDisplayName(roleCode: string): string {
    return this.translate.instant(`roles.${roleCode}`);
  }

  onColorPickerChange(event: Event): void {
    const value = (event.target as HTMLInputElement).value.toUpperCase();
    this.form.controls.color.setValue(value);
    this.form.controls.color.markAsTouched();
  }

  onSave(): void {
    const brandingGeaendert = this.hasChanges();
    if (brandingGeaendert) {
      this.form.markAllAsTouched();
      if (this.form.invalid) return;
    }
    if (this.kiUngueltig()) return;
    const kiSpeichern = this.kiGeaendert() ? this.kiSection()?.speichern$() : undefined;
    const aufrufe = [kiSpeichern, brandingGeaendert ? this.saveBranding() : undefined]
      .filter((aufruf): aufruf is Observable<void> => !!aufruf)
      .map((aufruf) =>
        aufruf.pipe(
          map(() => true),
          catchError(() => of(false)),
          defaultIfEmpty(null),
        ),
      );
    if (aufrufe.length === 0) return;

    forkJoin(aufrufe).subscribe((erfolge) => {
      // null: das Logo wurde abgelehnt, die Meldung steht schon am Logo.
      if (erfolge.includes(null) && !erfolge.includes(false)) return;
      const alleErfolgreich = erfolge.every(Boolean);
      this.snackBar.open(
        this.translate.instant(alleErfolgreich ? 'settings.saveSuccess' : 'settings.saveError'),
        undefined,
        {
          duration: 3000,
          verticalPosition: 'top',
          panelClass: alleErfolgreich ? 'snackbar-success' : 'snackbar-error',
        },
      );
    });
  }

  private saveBranding(): Observable<void> {
    const color = this.form.controls.color.value;
    const name = this.form.controls.name.value;
    const logo = this.pendingLogo();
    const upload$ = logo ? this.uploadLogo(logo.file) : of(false);

    return upload$.pipe(
      switchMap((hochgeladen) =>
        this.brandingApiService
          .updateBranding({
            name,
            headerColor: color,
            // Den Logo-Pfad setzt der Upload; nur Löschen schickt das Feld, sonst bliebe ein veralteter Client-Stand.
            logoPath: this.logoDeletePending() ? null : undefined,
          })
          .pipe(
            tap((data: BrandingData) => {
              this.headerColorService.loadFromBackend(data.headerColor);
              this.headerNameService.loadFromBackend(data.name);
              if (!hochgeladen) this.headerLogoService.loadFromBackend(data.logoPath);
              this.logoDeletePending.set(false);
              this.initialColor.set(color);
              this.initialName.set(name);
              this.form.markAsPristine();
            }),
          ),
      ),
      map(() => undefined),
    );
  }

  /** Ein abgelehntes Bild (400/413) beendet das Speichern still, die Meldung steht am Logo statt in der Snackbar. */
  private uploadLogo(file: File): Observable<boolean> {
    return this.brandingApiService.uploadLogo(file).pipe(
      tap((data) => {
        this.headerLogoService.loadFromBackend(data.logoPath);
        this.dropPendingLogo();
      }),
      map(() => true),
      catchError((err: unknown) => {
        const status = err instanceof HttpErrorResponse ? err.status : undefined;
        if (status !== 400 && status !== 413) return throwError(() => err);
        this.dropPendingLogo();
        this.logoError.set(status === 413 ? LOGO_TOO_LARGE_ERROR : LOGO_FORMAT_ERROR);
        return EMPTY;
      }),
    );
  }

  onLogoFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    this.dropPendingLogo();
    this.logoError.set(null);
    if (!BRANDING_LOGO_TYPES.includes(file.type)) {
      this.logoError.set(LOGO_FORMAT_ERROR);
      return;
    }
    if (file.size > LOGO_MAX_BYTES) {
      this.logoError.set(LOGO_TOO_LARGE_ERROR);
      return;
    }
    this.logoDeletePending.set(false);
    this.pendingLogo.set({ file, previewUrl: URL.createObjectURL(file) });
  }

  private dropPendingLogo(): void {
    const logo = this.pendingLogo();
    if (logo) URL.revokeObjectURL(logo.previewUrl);
    this.pendingLogo.set(null);
  }

  onCancel(): void {
    if (this.reiter() === 'ki') {
      this.kiSection()?.zuruecksetzen();
      return;
    }
    this.router.navigate(['/']);
  }

  onDiscard(): void {
    this.kiSection()?.zuruecksetzen();
    this.form.controls.color.setValue(this.initialColor());
    this.form.controls.name.setValue(this.initialName());
    this.logoDeletePending.set(false);
    this.dropPendingLogo();
    this.logoError.set(null);
    this.form.markAsPristine();
  }

  confirmDeactivate(): Observable<boolean> {
    this.showDeactivateConfirm.set(true);
    return this.deactivateConfirm$.pipe(take(1));
  }

  onDeactivateConfirmed(): void {
    this.showDeactivateConfirm.set(false);
    this.deactivateConfirm$.next(true);
  }

  onDeactivateCancelled(): void {
    this.showDeactivateConfirm.set(false);
    this.deactivateConfirm$.next(false);
  }

  onLogoDelete(): void {
    this.showDeleteModal.set(true);
  }

  onDeleteConfirm(): void {
    this.dropPendingLogo();
    this.logoError.set(null);
    this.logoDeletePending.set(true);
    this.showDeleteModal.set(false);
  }

  onDeleteCancel(): void {
    this.showDeleteModal.set(false);
  }
}
